import {
  collectionGroup,
  query,
  getDocs,
  doc,
  updateDoc,
  getDoc,
  onSnapshot,
} from "firebase/firestore";
import { db } from "config/firebase";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ProjectAssignment } from "@freetech/models/projects";
import { EversignTemplate } from "@freetech/models/eversign";
import { updateProjectAssignment } from "core/projectAssignments";
import { useHyperDxAction } from "@/hooks/common/useHyperDxAction";
import { apiClient } from "@/api/apiClient";
import { useEffect } from "react";
import { getCSAName } from "@/core/admin/clients";
import { useAdminClients } from "hooks/admin/useAdminClients";
import { usePublicUserList } from "hooks/user/usePublicUserList";

const { logError } = useHyperDxAction();

export type AdminProjectAssignment = {
  type: "projectAssignment";
  agreement: ProjectAssignment;
  id: string;
  projectName?: string;
  csaName?: string;
  freelancerName: string;
  clientName?: string;
};

export const useAdminProjectAssignments = () => {
  const queryClient = useQueryClient();
  const { clients } = useAdminClients();
  const { data: users } = usePublicUserList();


  const agreementsQuery = useQuery({
    queryKey: ["adminProjectAssignments"],
    queryFn: async () => {

      return [] as AdminProjectAssignment[];
    },
    staleTime: Infinity, // We don't want to refetch since we're using a snapshot listener
  });


  useEffect(() => {
    const projectAssignmentsQuery = query(
      collectionGroup(db, "projectAssignments")
    );

    const unsubscribe = onSnapshot(
      projectAssignmentsQuery,
      async (snapshot) => {
        const assignments = await Promise.all(
          snapshot.docs
            .map((doc) => {
              const data = doc.data();
              return {
                type: "projectAssignment" as const,
                agreement: {
                  ...data,
                  id: doc.id,
                } as ProjectAssignment,
                id: doc.id,
              };
            })
            .filter(
              (assignment) =>
                assignment.agreement.everSignDocumentStatus ===
                  "awaiting_admin_signature" ||
                assignment.agreement.everSignDocumentStatus ===
                  "awaiting_engineer_signature"
            )
            .map(async (assignment) => {
              let projectName = "";
              let csaName = "";
              let clientName = "";

              const projAss = assignment.agreement as ProjectAssignment;

              if (projAss.clientId && projAss.projectId && clients) {
                const client = clients.find(
                  (client) => client.id === projAss.clientId
                );
                projectName =
                  client?.projects?.find(
                    (project) => project.id === projAss.projectId
                  )?.name || "Unknown Project";
                if (projAss.csaId) {
                  try {
                    csaName =
                      (await getCSAName(
                        projAss.clientId,
                        projAss.projectId,
                        projAss.csaId
                      )) || "";
                  } catch (error) {
                    console.error("Error getting CSA name:", error);
                    csaName = "";
                  }
                }
                clientName = client?.companyName || "Unknown Client";
              }

              const freelancer = users?.find((user) => user.id === projAss.engineerId);

              return {
                ...assignment,
                projectName,
                csaName,
                freelancerName:
                  freelancer?.firstName + " " + freelancer?.lastName ||
                  "Unknown Freelancer",
                clientName,
              } as AdminProjectAssignment;
            })
        );

        queryClient.setQueryData(["adminProjectAssignments"], assignments);
      },
      (error) => {
        logError(error, {
          context: "useAdminProjectAssignments snapshot listener",
        });
        console.error("Error in snapshot listener:", error);
      }
    );


    return () => unsubscribe();
  }, [queryClient, clients, users]);


  const templatesQuery = useQuery<EversignTemplate[]>({
    queryKey: ["eversignTemplates"],
    queryFn: async () => {
      const response = await apiClient.eversign.admin.fetchTemplates();
      return response.templates;
    },
  });


  const updateProjectAssignmentMutation = useMutation({
    mutationFn: async ({
      clientId,
      assignmentId,
      updates,
    }: {
      clientId: string;
      assignmentId: string;
      updates: Partial<ProjectAssignment>;
    }) => {
      const projectAssignmentRef = doc(
        db,
        "clients",
        clientId,
        "projectAssignments",
        assignmentId
      );
      return updateDoc(projectAssignmentRef, updates);
    },
  });


  const cancelProjectAssignmentMutation = useMutation({
    mutationFn: async ({
      clientId,
      assignmentId,
      documentHash,
    }: {
      clientId: string;
      assignmentId: string;
      documentHash: string;
    }) => {
      const projectAssignmentRef = doc(
        db,
        "clients",
        clientId,
        "projectAssignments",
        assignmentId
      );

      await updateDoc(projectAssignmentRef, {
        everSignDocumentStatus: "cancelled",
        status: "cancelled",
      });

      await apiClient.eversign.admin.cancelDocument(documentHash);

      return true;
    },
  });

  return {
    data: agreementsQuery.data,
    isLoading: agreementsQuery.isLoading,
    isError: agreementsQuery.isError,
    error: agreementsQuery.error,


    templates: templatesQuery.data,
    isLoadingTemplates: templatesQuery.isLoading,
    templatesError: templatesQuery.error,


    updateProjectAssignment: (
      clientId: string,
      assignmentId: string,
      updates: Partial<ProjectAssignment>
    ) =>
      updateProjectAssignmentMutation.mutate({
        clientId,
        assignmentId,
        updates,
      }),
    isUpdatingProjectAssignment: updateProjectAssignmentMutation.isPending,
    updateProjectAssignmentError: updateProjectAssignmentMutation.error,

    cancelProjectAssignment: (
      clientId: string,
      assignmentId: string,
      documentHash: string
    ) =>
      cancelProjectAssignmentMutation.mutate({
        clientId,
        assignmentId,
        documentHash,
      }),
    isCancellingProjectAssignment: cancelProjectAssignmentMutation.isPending,
    cancelProjectAssignmentError: cancelProjectAssignmentMutation.error,
  };
};
